import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client.ts'
import { createKnowledgeBase, type KnowledgeBase } from '../../api/knowledgeBases.ts'
import { DEFAULT_PRESET_KEY, usablePresets } from './presets.ts'

interface CreateDialogProps {
  onClose: () => void
  onCreated: (base: KnowledgeBase) => void
}

/**
 * Create one base.
 *
 * The preset is chosen once and consumed at creation — the base then owns its own
 * conventions file — so this is the only moment the choice exists, and the dialog
 * says what each preset actually seeds rather than listing bare keys.
 */
export function CreateDialog({ onClose, onCreated }: CreateDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [presetKey, setPresetKey] = useState(DEFAULT_PRESET_KEY)
  const [customKey, setCustomKey] = useState('')
  const [useCustom, setUseCustom] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const presets = usablePresets()

  useEffect(() => {
    nameRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const effectiveKey = useCustom ? customKey.trim() : presetKey

  async function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || !effectiveKey) return
    setBusy(true)
    setError(null)
    try {
      const base = await createKnowledgeBase({
        name: trimmed,
        description: description.trim(),
        preset_key: effectiveKey,
      })
      onCreated(base)
    } catch (err) {
      setError(createFailureMessage(err, effectiveKey))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-kb-title"
        onSubmit={submit}
      >
        <div className="dialog__head">
          <h2 id="create-kb-title">Новая база знаний</h2>
          <p>
            Пресет задаёт, как база выглядит в первый день — её конвенции, скелет каталога и
            подкаталоги wiki. Дальше база живёт по своему <code>KB.md</code>, и его правите вы и
            агент.
          </p>
        </div>

        <div className="dialog__body">
          <div className="field">
            <label htmlFor="kb-name">Название</label>
            <input
              id="kb-name"
              ref={nameRef}
              className="input"
              value={name}
              maxLength={200}
              required
              onChange={(event) => setName(event.target.value)}
              placeholder="State Space Models"
            />
          </div>

          <div className="field">
            <label htmlFor="kb-desc">Описание</label>
            <textarea
              id="kb-desc"
              value={description}
              maxLength={4000}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Зачем эта база и какой у неё рабочий вопрос."
            />
          </div>

          <div className="field">
            <label>Пресет</label>
            <div className="presets">
              {presets.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className="preset"
                  aria-pressed={!useCustom && presetKey === preset.key}
                  onClick={() => {
                    setUseCustom(false)
                    setPresetKey(preset.key)
                  }}
                >
                  <span className="preset__key">{preset.key}</span>
                  <span className="preset__desc">
                    {preset.name} — {preset.description}
                  </span>
                </button>
              ))}
            </div>
            <div className="filters" style={{ marginTop: 4 }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-pressed={useCustom}
                onClick={() => setUseCustom((value) => !value)}
              >
                {useCustom ? 'Выбрать из списка' : 'Указать ключ вручную'}
              </button>
              {useCustom ? (
                <input
                  className="input input--mono"
                  value={customKey}
                  maxLength={200}
                  placeholder="preset_key"
                  onChange={(event) => setCustomKey(event.target.value)}
                  style={{ flex: 1, minWidth: 180 }}
                />
              ) : null}
            </div>
            <p className="field__hint">
              API не публикует каталог пресетов, поэтому список выше — это то, что платформа
              засеивает по умолчанию. Пресет, добавленный оператором, сюда не попадёт: его ключ
              можно ввести вручную.
            </p>
          </div>

          {error ? (
            <div className="callout callout--danger">
              <b>Не удалось создать базу.</b> {error}
            </div>
          ) : null}
        </div>

        <div className="dialog__foot">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || name.trim().length === 0 || effectiveKey.length === 0}
          >
            {busy ? 'Создаём…' : 'Создать базу'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <span className="muted-2" style={{ marginLeft: 'auto', fontSize: 11 }}>
            Пресет применяется один раз, при создании.
          </span>
        </div>
      </form>
    </div>
  )
}

/**
 * Turn the create failures into sentences that say what to do.
 *
 * Each of these statuses is a bare code with no body on this route, so the wording
 * is the only place the distinction survives — and they need different actions:
 * 409 is a name or a plan cap, 400 is the preset, 503 means nothing was created.
 */
function createFailureMessage(err: unknown, presetKey: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : 'неизвестная ошибка'
  }
  switch (err.status) {
    case 400:
      return `Пресет «${presetKey}» недоступен — проверьте ключ.`
    case 404:
      return 'Базы знаний выключены для этого аккаунта, либо у вас нет доступа к скоупу.'
    case 409:
      return 'Такое название уже занято, либо достигнут лимит числа баз по плану.'
    case 503:
      return 'Не удалось записать стартовые файлы базы. Ничего не создано — попробуйте ещё раз.'
    default:
      return err.message
  }
}
